import React, { useEffect } from 'react';
import { Moon, Sun, LogOut } from 'lucide-react';
import { clientNavItems } from '../../../config/clientNav';

interface MobileFlyoutProps {
  isOpen: boolean;
  onClose: () => void;
  currentView: string;
  onNavigate: (view: string) => void;
  userName: string;
  theme: string;
  onToggleTheme: () => void;
  onLogout: () => void;
}

export const MobileFlyout: React.FC<MobileFlyoutProps> = ({
  isOpen,
  onClose,
  currentView,
  onNavigate,
  userName,
  theme,
  onToggleTheme,
  onLogout,
}) => {
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  const items = clientNavItems.filter((item) => item.enabled);
  const initials = userName
    .split(' ')
    .map((part) => part.charAt(0))
    .join('')
    .slice(0, 2)
    .toUpperCase();

  const handleNavigate = (view: string) => {
    onNavigate(view);
    onClose();
  };

  return (
    <>
      <div
        className={`mc-flyout-backdrop ${isOpen ? 'open' : ''}`}
        onClick={onClose}
        aria-hidden="true"
      />
      <aside className={`mc-flyout ${isOpen ? 'open' : ''}`} role="dialog" aria-label="Navigation menu">
        <div className="mc-flyout-header">
          <div className="mc-flyout-avatar">{initials || 'MC'}</div>
          <div className="mc-flyout-user">
            <span className="mc-flyout-name">{userName || 'Client User'}</span>
            <span className="mc-flyout-role">CLIENT</span>
          </div>
        </div>

        <nav className="mc-flyout-nav">
          {items.map((item) => {
            const Icon = item.icon;
            const active = item.view === currentView;
            return (
              <button
                key={item.id}
                className={`mc-flyout-item ${active ? 'active' : ''}`}
                onClick={() => handleNavigate(item.view)}
              >
                <Icon size={20} />
                <span>{item.label}</span>
              </button>
            );
          })}
        </nav>

        <div className="mc-flyout-footer">
          <button className="mc-flyout-theme" onClick={onToggleTheme}>
            {theme === 'light' ? <Moon size={20} /> : <Sun size={20} />}
            <span>{theme === 'light' ? 'Dark' : 'Light'} Mode</span>
          </button>
          <button className="mc-flyout-logout" onClick={onLogout}>
            <LogOut size={20} />
            <span>Logout</span>
          </button>
        </div>
      </aside>
    </>
  );
};
